import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from "../../utils/permissionUtils";
import DataTable from "react-data-table-component";
import EditKnowledgeData from "./EditKnowledgeData";
import {
  Box,
  Typography,
  Alert,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Modal,
  Divider,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// Expanded Component for expandable rows
const ExpandedComponent = ({ data }) => {
  const theme = useTheme();
  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
      {data.type === "website_link" && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Website Link:</strong> {data.website_link}
          </Typography>
          <Typography variant="body2">
            <strong>Page Content:</strong> {data.page_content}
          </Typography>
        </>
      )}
      {data.type === "document" && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Filename:</strong> {data.filename}
          </Typography>
          <Typography variant="body2">
            <strong>Page Content:</strong> {data.page_content}
          </Typography>
        </>
      )}
      {data.type === "image" && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Filename:</strong> {data.filename}
          </Typography>
          {data.image_content && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Image Content:</strong> {data.image_content}
            </Typography>
          )}
          {data.content && (
            <Typography variant="body2">
              <strong>Image Content:</strong> {data.content}
            </Typography>
          )}
        </>
      )}
      {data.type === "voice" && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Filename:</strong> {data.filename}
          </Typography>
          <Typography variant="body2">
            <strong>Content:</strong> {data.content}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default function ListKnowledge({ refresh }) {
  const theme = useTheme();
  const { chatbotId: paramsChatbotId } = useParams();

  const [combinedData, setCombinedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentKnowledgeItem, setCurrentKnowledgeItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    document.title = "Knowledge Data | KlonIT Web Application";
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("knowledge", "view");
        const modifyPerm = await hasPermission("knowledge", "add|edit");
        const deletePerm = await hasPermission("knowledge", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      fetchKnowledgeData();
    }
  }, [refresh, triggerFetch, canView]);

  const fetchKnowledgeData = async () => {
    setLoading(true);
    setError("");
    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/get-knowleged`, { params });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      setCombinedData(responseData);
      setFilteredData(responseData);
    } catch (error) {
      console.error("Error fetching knowledge data:", error);
      setError("Failed to fetch knowledge data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
    setFilteredData(
      combinedData.filter((item) => {
        return (
          (item.type && item.type.toLowerCase().includes(value)) ||
          (item.website_link &&
            item.website_link.toLowerCase().includes(value)) ||
          (item.filename && item.filename.toLowerCase().includes(value)) ||
          (item.page_content &&
            item.page_content.toLowerCase().includes(value)) ||
          (item.image_content &&
            item.image_content.toLowerCase().includes(value)) ||
          (item.content && item.content.toLowerCase().includes(value)) ||
          (item.created_date &&
            new Date(item.created_date)
              .toLocaleDateString()
              .includes(value)) ||
          (item.upload_date &&
            new Date(item.upload_date).toLocaleDateString().includes(value))
        );
      })
    );
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to delete this ${row.type} item?`)) {
      return;
    }

    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      await axiosInstance.delete(`/delete-knowleged/${row.type}/${row.id}`, {
        params,
      });
      setCombinedData((prev) =>
        prev.filter((item) => item.id !== row.id || item.type !== row.type)
      );
      setFilteredData((prev) =>
        prev.filter((item) => item.id !== row.id || item.type !== row.type)
      );
      setError(`${row.type} item deleted successfully.`);
      setTriggerFetch(!triggerFetch);
    } catch (error) {
      console.error("Error deleting knowledge data:", error);
      setError(`Failed to delete ${row.type} item.`);
    }
  };

  const handleEdit = (row) => {
    setCurrentKnowledgeItem(row);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentKnowledgeItem(null);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setTriggerFetch(!triggerFetch);
  };

  const updateAfterEdit = () => {
    setTriggerFetch(!triggerFetch);
    closeEditModal();
  };

  // Function to get color based on data type
  const getDataTypeColor = (type) => {
    switch (type) {
      case "website_link":
        return theme.palette.mode === "dark" ? "#ef5350" : "#d32f2f";
      case "document":
        return theme.palette.mode === "dark" ? "#66bb6a" : "#388e3c";
      case "image":
        return theme.palette.mode === "dark" ? "#ff7043" : "#d84315";
      case "voice":
        return theme.palette.mode === "dark" ? "#9ccc65" : "#689f38";
      default:
        return theme.palette.mode === "dark" ? "#78909c" : "#546e7a";
    }
  };

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const columns = [
    {
      name: "Source/Filename",
      selector: (row) => row.website_link || row.filename,
      sortable: true,
      maxWidth: "300px",
    },
    {
      name: "Content Preview",
      selector: (row) => row.page_content || row.image_content || row.content,
      sortable: true,
      maxWidth: "350px",
    },
    {
      name: "Data Type",
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => (
        <Chip
          label={row.type}
          size="small"
          sx={{
            backgroundColor: getDataTypeColor(row.type),
            color: "white",
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      name: "Date",
      selector: (row) => row.created_date || row.upload_date,
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          {canModify && (
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEdit(row)}
            >
              <EditIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(row)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      ignoreRowClick: true,
    },
  ];

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
        <CircularProgress size={30} />
        <Typography mt={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6" fontWeight={700}>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to view Knowledge data. Please contact your
            administrator.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{
          p: 3,
          mt: 10,
          mx: "auto",
          maxWidth: "1200px",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
          Knowledge <strong>Data</strong>
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box>
          {selectedItem && (
            <EditKnowledgeData
              knowledgeItem={selectedItem}
              onClose={handleCloseModal}
              onUpdate={updateAfterEdit}
              chatbotId={paramsChatbotId}
              permission={{
                canView: canView,
                canModify: canModify,
                canDelete: canDelete,
              }}
            />
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="text.primary">
              Manage and organize your chatbot's knowledge base
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search..."
              value={searchText}
              onChange={handleSearch}
              sx={{ minWidth: "250px" }}
            />
          </Box>

          {error && (
            <Alert
              severity={
                error.includes("deleted") || error.includes("successfully")
                  ? "success"
                  : "error"
              }
              sx={{ mb: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={30} />
              <Typography mt={2}>Loading knowledge data...</Typography>
            </Box>
          ) : filteredData.length === 0 ? (
            <Box textAlign="center" py={4}>
              <i className="fa fa-database fa-3x text-muted" />
              <Typography mt={2} color="text.primary">
                No knowledge data available
              </Typography>
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              responsive
              highlightOnHover
              pointerOnHover
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              customStyles={customDataTableStyles}
              theme={theme.palette.mode === "dark" ? "dark" : "default"}
            />
          )}
        </Box>
      </Paper>

      {/* Edit Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={closeEditModal}
        aria-labelledby="edit-knowledge-modal"
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "80%", md: "900px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            outline: "none",
            overflow: "auto",
          }}
        >
          <EditKnowledgeData
            knowledgeItem={currentKnowledgeItem}
            onClose={closeEditModal}
            onUpdate={updateAfterEdit}
            chatbotId={paramsChatbotId}
            permission={{
              canView: canView,
              canModify: canModify,
              canDelete: canDelete,
            }}
          />
        </Box>
      </Modal>
    </>
  );
}